import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Award, Brain, CheckCircle, Clock, MessageSquare, RefreshCcw, Trophy, AlertCircle } from "lucide-react";

export default function Result() {
    const { submissionId } = useParams();
    const [submission, setSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [polling, setPolling] = useState(false);

    const load = async () => {
        const { data } = await api.get(`/exam/submission/${submissionId}`);
        setSubmission(data);
        setLoading(false);
    };

    useEffect(() => { load().catch(() => setLoading(false)); }, [submissionId]);

    // Poll for AI grade if essay submitted but not graded
    useEffect(() => {
        if (!submission) return;
        const needPoll = submission.essay_text && submission.ai_essay_score == null;
        if (!needPoll) return;
        setPolling(true);
        const intv = setInterval(async () => {
            try {
                const { data } = await api.get(`/exam/submission/${submissionId}`);
                setSubmission(data);
                if (data.ai_essay_score != null) {
                    clearInterval(intv);
                    setPolling(false);
                }
            } catch { /* ignore */ }
        }, 4000);
        return () => clearInterval(intv);
    }, [submission, submissionId]);

    if (loading) return <main className="pt-32 container-x"><div className="h-64 card shimmer" /></main>;
    if (!submission) return <main className="pt-32 container-x text-center"><h2>Không tìm thấy bài thi</h2></main>;

    const mcq = submission.mcq_score_pct ?? (submission.mcq_score / submission.mcq_max * 100);
    const ai = submission.ai_essay_score;

    return (
        <main className="pt-24 pb-12 min-h-screen">
            <div className="container-x max-w-5xl">
                {/* Hero */}
                <div className="text-center mb-10 fade-up">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[var(--gold)]/10 border-2 border-[var(--gold)]/40 mb-4">
                        <Trophy className="w-10 h-10 text-[var(--gold-deep)]" />
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold">Đã nộp bài thành công</h1>
                    <p className="text-slate-500 mt-2">{submission.contest?.title}</p>
                    <div className="mt-3 flex flex-wrap justify-center gap-2">
                        <span className="badge badge-gold">Trạng thái: {statusLabel(submission.status)}</span>
                        <span className="badge badge-slate"><Clock className="w-3 h-3" /> {Math.floor(submission.time_taken_seconds / 60)}p {submission.time_taken_seconds % 60}s</span>
                    </div>
                </div>

                {/* Score cards */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <ScoreCard icon={<CheckCircle />} title="Trắc nghiệm" value={`${submission.mcq_score}/${submission.mcq_max}`} pct={mcq} color="cyan" />
                    <ScoreCard icon={<Brain />} title="AI chấm tự luận"
                        value={ai != null ? `${Number(ai).toFixed(1)}/100` : "Đang chấm…"}
                        pct={ai ?? 0} color="cyan"
                        loading={ai == null && submission.essay_text} />
                    <ScoreCard icon={<Award />} title="Điểm chính thức"
                        value={submission.final_score != null ? `${submission.final_score}/100` : "Chờ BGK"}
                        pct={submission.final_score ?? 0} color="gold" />
                </div>

                {/* AI feedback */}
                {submission.essay_text && (
                    <div className="card p-7 mb-6 fade-up delay-1">
                        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                            <div className="flex items-center gap-2">
                                <Brain className="w-5 h-5 text-[var(--gold-deep)]" />
                                <h2 className="font-heading text-xl font-semibold">Phản hồi AI tự luận</h2>
                                {polling && <span className="badge badge-gold animate-pulse"><RefreshCcw className="w-3 h-3 animate-spin" /> Đang chấm</span>}
                            </div>
                            {submission.ai_provider_used && <span className="badge badge-slate">Engine: {submission.ai_provider_used}</span>}
                        </div>

                        {ai != null ? (
                            <>
                                <div className="mb-4">
                                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                                        <span>Điểm AI</span>
                                        <span>Độ tin cậy: {((submission.ai_essay_confidence ?? 0) * 100).toFixed(0)}%</span>
                                    </div>
                                    <div className="h-2 bg-[var(--bg-alt)] rounded-full overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-cyan-400 to-cyan-200" style={{ width: `${ai}%` }} />
                                    </div>
                                </div>

                                {submission.ai_essay_breakdown && (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                                        {Object.entries(submission.ai_essay_breakdown).map(([k, v]) => (
                                            <div key={k} className="border border-[var(--border)] rounded-xl p-3">
                                                <div className="text-xs text-slate-500 uppercase tracking-widest">{breakdownLabel(k)}</div>
                                                <div className="text-lg font-heading font-bold text-[var(--gold-deep)] mt-1">{Number(v).toFixed(1)}<span className="text-xs text-slate-500 ml-1">/25</span></div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="border-t border-[var(--border)] pt-4">
                                    <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2"><MessageSquare className="w-3 h-3" /> Nhận xét chi tiết</div>
                                    <p className="text-slate-700 leading-relaxed">{submission.ai_essay_feedback}</p>
                                </div>

                                {submission.ai_essay_flags?.length > 0 && (
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {submission.ai_essay_flags.map((f, i) => (
                                            <span key={i} className="badge badge-red"><AlertCircle className="w-3 h-3" /> {f}</span>
                                        ))}
                                    </div>
                                )}

                                <div className="mt-4 text-xs text-slate-500 italic border-t border-[var(--border)] pt-3">
                                    Đây là kết quả AI chấm sơ bộ. Điểm chính thức do Ban Giám khảo công bố sau khi review.
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-8">
                                <div className="w-10 h-10 mx-auto mb-3 rounded-full border-2 border-[var(--gold)] border-t-transparent animate-spin" />
                                <p className="text-slate-500">Hệ thống AI đang phân tích bài viết của bạn…</p>
                            </div>
                        )}
                    </div>
                )}

                {submission.jury_essay_score != null && (
                    <div className="card p-7 mb-6 border border-[var(--gold)]/30">
                        <div className="flex items-center gap-2 mb-3">
                            <Award className="w-5 h-5 text-[var(--gold-deep)]" />
                            <h2 className="font-heading text-xl font-semibold">Điểm Ban Giám khảo</h2>
                        </div>
                        <div className="text-3xl font-heading font-bold text-[var(--gold-deep)]">{submission.jury_essay_score}/100</div>
                        {submission.jury_notes && <p className="text-slate-600 mt-3">{submission.jury_notes}</p>}
                    </div>
                )}

                <div className="flex flex-wrap gap-3 justify-center">
                    <Link to={`/bang-xep-hang/${submission.contest_id}`} className="btn-primary" data-testid="see-leaderboard">
                        <Trophy className="w-4 h-4" /> Xem bảng xếp hạng
                    </Link>
                    <Link to="/cuoc-thi" className="btn-secondary">Các cuộc thi khác</Link>
                </div>
            </div>
        </main>
    );
}

function ScoreCard({ icon, title, value, pct, color = "cyan", loading }) {
    const isGold = color === "gold";
    return (
        <div className={`card p-6 relative ${loading ? "" : "fade-up"}`}>
            <div className={`flex items-center gap-2 mb-3 ${isGold ? "text-yellow-400" : "text-[var(--gold-deep)]"} [&_svg]:w-4 [&_svg]:h-4`}>{icon}
                <span className="text-xs uppercase tracking-widest">{title}</span>
            </div>
            <div className="text-3xl font-heading font-bold">{value}</div>
            {!loading && pct > 0 && (
                <div className="h-1.5 bg-[var(--bg-alt)] rounded-full mt-4 overflow-hidden">
                    <div className={`h-full ${isGold ? "bg-yellow-400" : "bg-gradient-to-r from-cyan-400 to-cyan-200"}`} style={{ width: `${pct}%` }} />
                </div>
            )}
            {loading && <div className="h-1.5 bg-[var(--bg-alt)] rounded-full mt-4 shimmer" />}
        </div>
    );
}

function statusLabel(s) {
    return { submitted: "Đã nộp", ai_graded: "AI đã chấm", jury_reviewed: "BGK đã xem", finalized: "Chính thức" }[s] || s;
}
function breakdownLabel(k) {
    return { relevance: "Liên quan", knowledge: "Kiến thức", reasoning: "Lập luận", communication: "Truyền thông" }[k] || k;
}
